import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import getAssetImageUrl from '../utils/assetImageUrl';
import formatAssetId from '../utils/formatAssetId';

// Helper function to get image filename based on asset type
function getImageForType(assetType) {
  if (!assetType) return 'Pallets.png';
  const typeMap = {
    'Racks': 'Racks.png',
    'Racks (Large)': 'Racks (Large).png',
    'Shipping Rack': 'Shipping Rack.png',
    'Material Handling (Rack)': 'Material Handling (Rack).png',
    'Metal Storage (Rack)': 'Metal Storage (Rack).png',
    'Warehouse (Rack)': 'Warehouse (Rack).png',
    'Automotive Parts (Rack)': 'Automotive Parts (Rack).png',
    'Dunnage (Rack)': 'Dunnage (Rack).png',
    'Steel Rack (Dunnage)': 'Steel Rack (Dunnage).png',
    'Bins (Large)': 'Bins (Large).png',
    'Bins (Medium)': 'Bins (Medium).png',
    'Bins (Small)': 'Bins (Small).png',
    'Bins': 'Bins (Medium).png',
    'Returnable (Bin)': 'Returnable (Bin).png',
    'Industrial Scrap (Bin)': 'Industrial Scrap (Bin).png',
    'Metal Scrap Large (Bin)': 'Metal Scrap Large (Bin).png',
    'Metal Scrap Small (Bin)': 'Metal Scrap Small (Bin).png',
    'Metal Stacking (Bin)': 'Metal Stacking (Bin).png',
    'Crates': 'Crates.png',
    'Crates (Large)': 'Crates (Large).png',
    'Crates (Small)': 'Crates (Small).png',
    'Totes (Large)': 'Totes (Large).png',
    'Totes (Medium)': 'Totes (Medium).png',
    'Totes (Small)': 'Totes (Small).png',
    'Totes': 'Totes (Medium).png',
    'Pallets': 'Pallets.png',
    'Automotive Packaging (Dunnage)': 'Automotive Packaging (Dunnage).png',
    'Custom Foam (Dunnage)': 'Custom Foam (Dunnage).png',
    'Plastic (Dunnage)': 'Plastic (Dunnage).png',
    'Returnable Package (Dunnage)': 'Returnable Package (Dunnage).png'
  };
  return typeMap[assetType] || 'Pallets.png';
}

// Unique color palette for warehouses
const WAREHOUSE_COLORS = [
  { border: 'border-emerald-500', bg: 'bg-emerald-600/20', name: 'emerald' },
  { border: 'border-amber-500', bg: 'bg-amber-600/20', name: 'amber' },
  { border: 'border-violet-500', bg: 'bg-violet-600/20', name: 'violet' },
  { border: 'border-rose-500', bg: 'bg-rose-600/20', name: 'rose' },
  { border: 'border-cyan-500', bg: 'bg-cyan-600/20', name: 'cyan' },
  { border: 'border-orange-500', bg: 'bg-orange-600/20', name: 'orange' },
  { border: 'border-teal-500', bg: 'bg-teal-600/20', name: 'teal' },
  { border: 'border-pink-500', bg: 'bg-pink-600/20', name: 'pink' },
  { border: 'border-indigo-500', bg: 'bg-indigo-600/20', name: 'indigo' },
  { border: 'border-lime-500', bg: 'bg-lime-600/20', name: 'lime' },
];

// Main warehouse uses a distinct gold/yellow color
const MAIN_WAREHOUSE_COLOR = { border: 'border-yellow-400', bg: 'bg-yellow-500/20', name: 'gold' };

export default function Sidebar({ data, isGoogleView = false, selectedZoneId, selectedRackId, selectedAssetId, selectedAssetType, onZoneSelect, onAssetSelect, onRackSelect, onAssetTypeSelect, warehouses = {}, onWarehouseClick = null, assetStats = null }) {
  const { isDark } = useTheme();
  const [query, setQuery] = useState('');
  const [expandedZoneId, setExpandedZoneId] = useState(null);
  const [expandedRackId, setExpandedRackId] = useState(null);
  const [assetsExpanded, setAssetsExpanded] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const zonesById = useMemo(() => Object.fromEntries((data?.zones || []).map(z => [z.id, z])), [data?.zones]);
  
  // Get color for a warehouse based on its index (for partner warehouses)
  const getWarehouseColor = (index) => WAREHOUSE_COLORS[index % WAREHOUSE_COLORS.length];

  // Generate search suggestions (only for warehouse view)
  const searchSuggestions = useMemo(() => {
    if (isGoogleView || !query || query.length < 1) return [];
    
    const q = query.toLowerCase().trim();
    const suggestions = [];
    
    // Search assets by name/ID
    (data?.assets || []).forEach(asset => {
      const assetName = (asset.name || asset.id || '').toLowerCase();
      const assetId = (asset.id || '').toLowerCase();
      const assetType = (asset.assetType || asset.type || '').toLowerCase();
      
      if (assetName.includes(q) || assetId.includes(q) || assetType.includes(q)) {
        suggestions.push({
          type: 'asset',
          id: asset.id,
          label: formatAssetId(asset),
          subtitle: asset.assetType || asset.type || 'Asset',
          asset: asset
        });
      }
    });
    
    // Search zones
    (data?.zones || []).forEach(zone => {
      const zoneName = (zone.name || zone.displayName || zone.id || '').toLowerCase();
      if (zoneName.includes(q) && !zone.gps?.polygon) { // Exclude warehouse zones
        suggestions.push({
          type: 'zone',
          id: zone.id,
          label: zone.name || zone.displayName || zone.id,
          subtitle: 'Zone'
        });
      }
    });
    
    // Search asset types
    const uniqueTypes = [...new Set((data?.assets || []).map(a => a.assetType || a.type).filter(Boolean))];
    uniqueTypes.forEach(type => {
      if (type.toLowerCase().includes(q)) {
        suggestions.push({
          type: 'assetType',
          id: type,
          label: type,
          subtitle: 'Asset Type'
        });
      }
    });
    
    // Remove duplicates and limit to 8 results
    const seen = new Set();
    return suggestions
      .filter(s => {
        const key = `${s.type}-${s.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 8);
  }, [query, data, isGoogleView]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback((suggestion) => {
    if (suggestion.type === 'asset' && onAssetSelect) {
      onAssetSelect(suggestion.id);
    } else if (suggestion.type === 'zone' && onZoneSelect) {
      onZoneSelect(suggestion.id);
    } else if (suggestion.type === 'assetType' && onAssetTypeSelect) {
      onAssetTypeSelect(suggestion.id);
    }
    setQuery('');
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
  }, [onAssetSelect, onZoneSelect, onAssetTypeSelect]);

  // Handle keyboard navigation in suggestions
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showSuggestions || searchSuggestions.length === 0) return;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < searchSuggestions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
      } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
        e.preventDefault();
        handleSuggestionSelect(searchSuggestions[selectedSuggestionIndex]);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    };
    
    if (showSuggestions) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [showSuggestions, searchSuggestions, selectedSuggestionIndex, handleSuggestionSelect]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          searchInputRef.current && !searchInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    
    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  // Helper function to check if a point is inside a zone's rect
  const isPointInZone = (position, zone) => {
    if (!position || !position.x || !position.y || !zone.rect) return false;
    const { x, y, width, height } = zone.rect;
    return position.x >= x && position.x <= x + width &&
           position.y >= y && position.y <= y + height;
  };

  // Group assets by zone based on position (if inside zone rect) or zoneId/rowId
  const groupedAssets = useMemo(() => {
    const q = query.toLowerCase();
    const filteredAssets = (data?.assets || []).filter(a => {
      // Only include assets with an active PLX tag (fiducial_marker_id)
      // Only show assets that have a fiducial_marker_id value (not null or undefined)
      // Allow 0 as a valid marker ID
      if (a.fiducial_marker_id == null) return false;
      return a.id.toLowerCase().includes(q);
    });
    
    const zoneMap = new Map();
    for (const z of (data?.zones || [])) {
      const rackMap = new Map();
      // Initialize with all racks for this zone
      if (z.rows) {
        for (const rack of z.rows) {
          rackMap.set(rack.id, []);
        }
      }
      // Also add a "no-rack" category for assets in zone but not in a specific rack
      rackMap.set('_no_rack', []);
      zoneMap.set(z.id, rackMap);
    }
    
    // Group assets by zone - check position first, then fall back to zoneId
    for (const a of filteredAssets) {
      let assignedZone = null;
      
      // First, check if asset position is inside any zone's rect
      if (a.position && a.position.x !== undefined && a.position.y !== undefined) {
        for (const z of (data?.zones || [])) {
          if (isPointInZone(a.position, z)) {
            assignedZone = z;
            break;
          }
        }
      }
      
      // If not found by position, use the asset's zoneId property
      if (!assignedZone && a.zoneId) {
        assignedZone = zonesById[a.zoneId];
      }
      
      // If we found a zone, assign the asset to it
      if (assignedZone && zoneMap.has(assignedZone.id)) {
        const rackMap = zoneMap.get(assignedZone.id);
        
        // If asset has a rowId and that rack exists, put it there
        if (a.rowId && rackMap.has(a.rowId)) {
          rackMap.get(a.rowId).push(a);
        } else {
          // Otherwise, put it in the "no-rack" category
          rackMap.get('_no_rack').push(a);
        }
      }
    }
    
    return zoneMap; // Map<zoneId, Map<rackId, Asset[]>>
  }, [data?.assets, data?.zones, query, zonesById]);

  return (
    <div className={`rounded-2xl shadow-2xl backdrop-blur overflow-hidden border ${isDark ? 'bg-gray-900/70 border-gray-700' : 'bg-white/95 border-gray-200'}`}>
      {/* Asset Status Stats - Only show when not in Google View and assetStats provided */}
      {!isGoogleView && assetStats && (
        <div className={`p-3 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <div className={`text-xs uppercase mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</div>
          <div className="grid grid-cols-3 gap-2">
            <div className={`rounded-md p-2 text-center ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-0.5`}>In House</div>
              <div className={`text-lg font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>{assetStats.inHouse}</div>
            </div>
            <div className={`rounded-md p-2 text-center ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-0.5`}>At Partner</div>
              <div className={`text-lg font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{assetStats.atPartnerFacility}</div>
            </div>
            <div className={`rounded-md p-2 text-center ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-0.5`}>MIA</div>
              <div className={`text-lg font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>{assetStats.mia}</div>
            </div>
          </div>
        </div>
      )}
      <div className={`p-3 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Location</div>
        <div className="mt-2 relative">
          <input
            ref={searchInputRef}
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setShowSuggestions(e.target.value.length > 0 && !isGoogleView);
              setSelectedSuggestionIndex(-1);
            }}
            onFocus={() => {
              if (query.length > 0 && !isGoogleView) {
                setShowSuggestions(true);
              }
            }}
            placeholder={isGoogleView ? "Search asset ID..." : "Search asset name, type, or zone..."}
            className={`w-full px-3 py-2 rounded-md outline-none border ${
              isDark 
                ? 'bg-gray-800/70 text-gray-100 border-gray-700 focus:border-gray-500' 
                : 'bg-gray-50 text-gray-900 border-gray-200 focus:border-gray-400'
            }`}
          />
          {/* Search suggestions dropdown (warehouse view only) */}
          {!isGoogleView && showSuggestions && searchSuggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className={`absolute z-50 w-full mt-1 rounded-md shadow-lg border max-h-64 overflow-y-auto ${
                isDark 
                  ? 'bg-gray-800 border-gray-700' 
                  : 'bg-white border-gray-200'
              }`}
            >
              {searchSuggestions.map((suggestion, idx) => (
                <div
                  key={`${suggestion.type}-${suggestion.id}`}
                  className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${
                    idx === selectedSuggestionIndex
                      ? isDark ? 'bg-gray-700' : 'bg-gray-100'
                      : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  onMouseEnter={() => setSelectedSuggestionIndex(idx)}
                >
                  {suggestion.type === 'asset' && (
                    <img 
                      src={getAssetImageUrl(suggestion.asset?.image || '')} 
                      alt={suggestion.label} 
                      className="w-5 h-5 object-contain" 
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {suggestion.label}
                    </div>
                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {suggestion.subtitle}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="p-3 overflow-y-auto no-scrollbar max-h-[70vh] space-y-3">
        {isGoogleView ? (
          <>
            {/* Google Map View: Your Warehouse (Customer org type) */}
            {(() => {
              const mainWarehouses = Object.values(warehouses || {}).filter(wh => wh.gps && wh.gps.polygon && wh.org_type === 'Customer');
              if (mainWarehouses.length === 0) return null;
              
              return (
                <>
                  <div className={`text-xs uppercase flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span>Your Warehouse</span>
                    <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
                  </div>
                  <div className="mt-2 space-y-2">
                    {mainWarehouses.map(warehouse => {
                      const warehouseAssets = (data?.assets || []).filter(a => {
                        // Only include assets with an active PLX tag (fiducial_marker_id)
                        // Only show assets that have a fiducial_marker_id value (not null or undefined)
                        // Allow 0 as a valid marker ID
                        if (a.fiducial_marker_id == null) return false;
                        return a.warehouseId === warehouse.id || a.warehouse_id === warehouse.id;
                      });
                      const assetCount = warehouseAssets.length;
                      
                      const assetGroups = {};
                      warehouseAssets.forEach(asset => {
                        const type = asset.assetType || 'Unknown';
                        assetGroups[type] = (assetGroups[type] || 0) + 1;
                      });
                      
                      // Calculate center of warehouse polygon for navigation
                      const getWarehouseCenter = () => {
                        if (!warehouse.gps?.polygon?.length) return null;
                        const lats = warehouse.gps.polygon.map(p => p.lat);
                        const lngs = warehouse.gps.polygon.map(p => p.lng);
                        return {
                          lat: (Math.min(...lats) + Math.max(...lats)) / 2,
                          lng: (Math.min(...lngs) + Math.max(...lngs)) / 2
                        };
                      };
                      
                      return (
                        <div
                          key={warehouse.id}
                          className={`rounded-md border-2 ${MAIN_WAREHOUSE_COLOR.border} cursor-pointer transition-all hover:shadow-md hover:scale-[1.02]`}
                          onClick={() => {
                            const center = getWarehouseCenter();
                            if (onWarehouseClick && center) {
                              onWarehouseClick(warehouse.id, center);
                            }
                          }}
                        >
                          <div className={`px-3 py-3 rounded-md ${MAIN_WAREHOUSE_COLOR.bg}`}>
                            <div className="flex items-center justify-between">
                              <div className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{warehouse.display_name || warehouse.name}</div>
                              <div className={`text-xs px-2 py-0.5 rounded ${isDark ? 'text-gray-200 bg-gray-900/30' : 'text-gray-700 bg-gray-100'}`}>{assetCount} assets</div>
                            </div>
                            {Object.keys(assetGroups).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {Object.entries(assetGroups).map(([type, count]) => (
                                  <div key={type} className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-900/40 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                                    {count} {type}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className={`text-xs mt-2 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              <span>📍</span> Click to navigate
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
            
            {/* Google Map View: Partners Warehouse (Vendor/Customer org types) */}
            {(() => {
              const partnerWarehouses = Object.values(warehouses || {}).filter(wh => 
                wh.gps && wh.gps.polygon && (wh.org_type === 'Vendor' || wh.org_type === 'Customer' || !wh.org_type)
              );
              if (partnerWarehouses.length === 0) return null;
              
              return (
                <>
                  <div className={`text-xs uppercase mt-4 flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span>Partners Warehouse</span>
                    <div className="flex gap-0.5">
                      {partnerWarehouses.slice(0, 3).map((_, i) => (
                        <span key={i} className={`w-2 h-2 rounded-full`} style={{ backgroundColor: ['#10b981', '#f59e0b', '#8b5cf6'][i] }}></span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-2 space-y-2">
                    {partnerWarehouses.map((warehouse, index) => {
                      const color = getWarehouseColor(index);
                      const warehouseAssets = (data?.assets || []).filter(a => {
                        // Only include assets with an active PLX tag (fiducial_marker_id)
                        // Only show assets that have a fiducial_marker_id value (not null or undefined)
                        // Allow 0 as a valid marker ID
                        if (a.fiducial_marker_id == null) return false;
                        return a.warehouseId === warehouse.id || a.warehouse_id === warehouse.id;
                      });
                      const assetCount = warehouseAssets.length;
                      
                      const assetGroups = {};
                      warehouseAssets.forEach(asset => {
                        const type = asset.assetType || 'Unknown';
                        assetGroups[type] = (assetGroups[type] || 0) + 1;
                      });
                      
                      // Calculate center of warehouse polygon for navigation
                      const getWarehouseCenter = () => {
                        if (!warehouse.gps?.polygon?.length) return null;
                        const lats = warehouse.gps.polygon.map(p => p.lat);
                        const lngs = warehouse.gps.polygon.map(p => p.lng);
                        return {
                          lat: (Math.min(...lats) + Math.max(...lats)) / 2,
                          lng: (Math.min(...lngs) + Math.max(...lngs)) / 2
                        };
                      };
                      
                      return (
                        <div
                          key={warehouse.id}
                          className={`rounded-md border-2 ${color.border} cursor-pointer transition-all hover:shadow-md hover:scale-[1.02]`}
                          onClick={() => {
                            const center = getWarehouseCenter();
                            if (onWarehouseClick && center) {
                              onWarehouseClick(warehouse.id, center);
                            }
                          }}
                        >
                          <div className={`px-3 py-3 rounded-md ${color.bg}`}>
                            <div className="flex items-center justify-between">
                              <div className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{warehouse.display_name || warehouse.name}</div>
                              <div className={`text-xs px-2 py-0.5 rounded ${isDark ? 'text-gray-200 bg-gray-900/30' : 'text-gray-700 bg-gray-100'}`}>{assetCount} assets</div>
                            </div>
                            {warehouse.org_name && (
                              <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{warehouse.org_type}: {warehouse.org_name}</div>
                            )}
                            {Object.keys(assetGroups).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {Object.entries(assetGroups).map(([type, count]) => (
                                  <div key={type} className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-900/40 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                                    {count} {type}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className={`text-xs mt-2 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              <span>📍</span> Click to navigate
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
            
            {/* Google Map View: Show Assets (grouped by warehouse) */}
            {Object.keys(warehouses || {}).length > 0 && (
              <>
                <div className={`text-xs uppercase mt-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Assets</div>
                <div className="mt-2 space-y-2">
                  {Object.values(warehouses || {}).map(warehouse => {
                    const warehouseAssets = (data?.assets || []).filter(a => {
                      // Only include assets with an active PLX tag (fiducial_marker_id)
                      // Only show assets that have a fiducial_marker_id value (not null or undefined)
                      // Allow 0 as a valid marker ID
                      if (a.fiducial_marker_id == null) return false;
                      return a.warehouseId === warehouse.id;
                    });
                    if (warehouseAssets.length === 0) return null;
                    
                    return (
                      <div key={warehouse.id} className="mb-3">
                        <div className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{warehouse.display_name || warehouse.name}</div>
                        <div className="space-y-1">
                          {warehouseAssets.map(asset => (
                            <div
                              key={asset.id}
                              className={`rounded-md p-2 text-sm cursor-pointer transition-colors ${
                                selectedAssetId === asset.id
                                  ? 'bg-blue-900/40 border border-blue-500'
                                  : isDark 
                                    ? 'bg-gray-700/60 hover:bg-gray-700 border border-gray-700' 
                                    : 'bg-gray-100 hover:bg-gray-200 border border-gray-200'
                              }`}
                              onClick={() => onAssetSelect && onAssetSelect(selectedAssetId === asset.id ? null : asset.id)}
                            >
                              <div className="flex items-center gap-2">
                                <img 
                                  src={getAssetImageUrl(asset.image || (asset.assetType ? getImageForType(asset.assetType) : 'Pallets.png'))} 
                                  alt={asset.assetType} 
                                  className="w-5 h-5 object-contain"
                                  onError={(e) => {
                                    // Fallback to default image if the image fails to load
                                    if (e.target.src !== getAssetImageUrl('Pallets.png')) {
                                      e.target.src = getAssetImageUrl('Pallets.png');
                                    }
                                  }}
                                />
                                <span className={isDark ? 'text-gray-100' : 'text-gray-900'}>{formatAssetId(asset)}</span>
                                {asset.assetType && (
                                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>({asset.assetType})</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {/* Warehouse View: Show Zones */}
            <div className={`text-xs uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Zones</div>
            <div className="mt-2 space-y-2">
              {(data?.zones || []).filter(z => {
                if (!z.rect || z.rect.width <= 0 || z.rect.height <= 0) return false;
                // Exclude zones with GPS polygons (these are warehouses, not zones)
                if (z.gps && z.gps.polygon) return false;
                // Also filter by known warehouse-like names
                const name = (z.name || '').toLowerCase();
                return !(name.includes('gametime') || 
                        name.includes('childrens place') || 
                        name.includes('children') ||
                        name.includes('gh metals') ||
                        name.includes('gh-metals'));
              }).map(z => {
                const rackMap = groupedAssets.get(z.id) || new Map();
                const totalAssets = Array.from(rackMap.values()).flat().length;
                const isExpanded = expandedZoneId === z.id;
                
                return (
                  <div key={z.id} className="rounded-md overflow-hidden">
                    <div
                      className="px-3 py-2 flex items-center justify-between cursor-pointer"
                      style={{ backgroundColor: z.color }}
                      onClick={() => {
                        setExpandedZoneId(prev => (prev === z.id ? null : z.id));
                        if (expandedZoneId === z.id) {
                          setExpandedRackId(null); // Close rack when closing zone
                        }
                      }}
                    >
                      <div className="text-sm font-medium text-white">{z.name} <span className="text-xs text-white/80">({totalAssets})</span></div>
                      <div className="text-xs text-white/80">{isExpanded ? 'Hide' : 'Show'}</div>
                    </div>
                    {isExpanded && (
                      <div className={`p-2 space-y-2 max-h-60 overflow-y-auto no-scrollbar ${isDark ? 'bg-gray-900/60' : 'bg-gray-50'}`}>
                        {z.rows && z.rows.length > 0 ? (
                          <>
                            {z.rows.map(rack => {
                              const rackAssets = rackMap.get(rack.id) || [];
                              const isRackExpanded = expandedRackId === rack.id;
                              
                              return (
                                <div key={rack.id} className={`rounded-md border ${isDark ? 'bg-gray-800/40 border-gray-700' : 'bg-white border-gray-200'}`}>
                                  <div
                                    className={`px-3 py-2 flex items-center justify-between cursor-pointer ${isDark ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50'}`}
                                    onClick={() => {
                                      setExpandedRackId(prev => (prev === rack.id ? null : rack.id));
                                      // Select rack to ping all assets in it
                                      onRackSelect(rack.id);
                                    }}
                                  >
                                    <div className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                      Row {rack.id.split('-')[1]} <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>({rackAssets.length})</span>
                                    </div>
                                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{isRackExpanded ? 'Hide' : 'Show'}</div>
                                  </div>
                                  {isRackExpanded && (
                                    <div className={`p-2 space-y-1 ${isDark ? 'bg-gray-900/40' : 'bg-gray-50'}`}>
                                      {rackAssets.length === 0 ? (
                                        <div className={`text-xs px-2 py-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No assets in this rack.</div>
                                      ) : (
                                        rackAssets.map(a => (
                                          <div
                                            key={a.id}
                                            className={`rounded-md p-2 cursor-pointer ${isDark ? 'bg-gray-700/60 hover:bg-gray-700' : 'bg-white hover:bg-gray-100 border border-gray-200'}`}
                                            onClick={() => {
                                              onAssetSelect(a.id);
                                            }}
                                          >
                                            <div className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{a.id}</div>
                                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{a.status} • {a.assetType}</div>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {/* Show assets in zone but not in a specific rack */}
                            {(() => {
                              const noRackAssets = rackMap.get('_no_rack') || [];
                              if (noRackAssets.length === 0) return null;
                              
                              return (
                                <div className={`rounded-md border ${isDark ? 'bg-gray-800/40 border-gray-700' : 'bg-white border-gray-200'}`}>
                                  <div className={`px-3 py-2 text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                    Other Assets <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>({noRackAssets.length})</span>
                                  </div>
                                  <div className={`p-2 space-y-1 ${isDark ? 'bg-gray-900/40' : 'bg-gray-50'}`}>
                                    {noRackAssets.map(a => (
                                      <div
                                        key={a.id}
                                        className={`rounded-md p-2 cursor-pointer ${isDark ? 'bg-gray-700/60 hover:bg-gray-700' : 'bg-white hover:bg-gray-100 border border-gray-200'}`}
                                        onClick={() => {
                                          onAssetSelect(a.id);
                                        }}
                                      >
                                        <div className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{a.id}</div>
                                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{a.status} • {a.assetType}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <>
                            {/* If no rows defined, show all assets in zone directly */}
                            {(() => {
                              const noRackAssets = rackMap.get('_no_rack') || [];
                              if (noRackAssets.length === 0) {
                                return <div className={`text-xs px-2 py-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No assets in this zone.</div>;
                              }
                              
                              return (
                                <div className="space-y-1">
                                  {noRackAssets.map(a => (
                                    <div
                                      key={a.id}
                                      className={`rounded-md p-2 cursor-pointer ${isDark ? 'bg-gray-700/60 hover:bg-gray-700' : 'bg-white hover:bg-gray-100 border border-gray-200'}`}
                                      onClick={() => {
                                        onAssetSelect(a.id);
                                      }}
                                    >
                                      <div className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{a.id}</div>
                                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{a.status} • {a.assetType}</div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Assets Section - Different behavior for Google vs Warehouse view */}
        {isGoogleView ? (
          // Google Map View: Show assets only when a zone is selected
          selectedZoneId && (() => {
            const zoneAssets = (data?.assets || []).filter(a => a.zoneId === selectedZoneId);
            const selectedZone = zonesById[selectedZoneId];
            const zoneName = selectedZone?.displayName || selectedZone?.name || 'Selected Zone';
            return (
              <div className="pt-2">
                <div className={`text-xs uppercase flex items-center justify-between ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>Assets in {zoneName}</span>
                  <button className={`text-[11px] ${isDark ? 'text-gray-300 hover:text-gray-100' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setAssetsExpanded(v => !v)}>
                    {assetsExpanded ? 'Hide' : 'Show'}
                  </button>
                </div>
                {assetsExpanded && (
                  <div className="mt-2 space-y-2 max-h-80 overflow-y-auto no-scrollbar">
                    {zoneAssets.map(asset => (
                      <div
                        key={asset.id}
                        className={`rounded-md p-2 cursor-pointer border ${isDark ? 'bg-gray-700/60 hover:bg-gray-700 border-gray-700' : 'bg-gray-100 hover:bg-gray-200 border-gray-200'}`}
                        onClick={() => onAssetSelect(asset.id)}
                      >
                        <div className="flex items-center gap-2">
                          <img 
                            src={getAssetImageUrl(asset.image || (asset.assetType ? getImageForType(asset.assetType) : 'Pallets.png'))} 
                            alt={asset.assetType} 
                            className="w-5 h-5 object-contain"
                            onError={(e) => {
                              // Fallback to default image if the image fails to load
                              if (e.target.src !== getAssetImageUrl('Pallets.png')) {
                                e.target.src = getAssetImageUrl('Pallets.png');
                              }
                            }}
                          />
                          <div className="flex-1">
                            <div className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{formatAssetId(asset)}</div>
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{asset.status} • {asset.assetType}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {zoneAssets.length === 0 && (
                      <div className={`text-xs px-2 py-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {selectedZoneId === 'zone-main' 
                          ? 'View assets in Warehouse View'
                          : 'No assets found in this zone.'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()
        ) : (
          // Warehouse View: Show asset types summary
          <div className="pt-2">
            <div className={`text-xs uppercase flex items-center justify-between ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <span>Assets</span>
              <button className={`text-[11px] ${isDark ? 'text-gray-300 hover:text-gray-100' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setAssetsExpanded(v => !v)}>
                {assetsExpanded ? 'Hide' : 'Show'}
              </button>
            </div>
            {assetsExpanded && (
              <div className="mt-2 grid grid-cols-1 gap-2">
                {Array.from(new Set((data?.assets || []).map(a => a.assetType))).sort().map(type => {
                  const allOfType = (data?.assets || []).filter(a => a.assetType === type);
                  const totalCount = allOfType.length;
                  return (
                    <button
                      key={type}
                      onClick={() => onAssetTypeSelect(type === selectedAssetType ? null : type)}
                      className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                        type === selectedAssetType
                          ? 'bg-blue-900/40 border-blue-500 text-blue-100'
                          : isDark 
                            ? 'bg-gray-800/40 border-gray-700 text-gray-200 hover:bg-gray-800/60'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <img src={getAssetImageUrl(allOfType[0]?.image || '')} alt={type} className="w-5 h-5 object-contain" />
                        <span className="text-sm">{type}</span>
                        <div className="ml-auto flex items-center gap-2 text-xs">
                          <span className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700/70 text-gray-200' : 'bg-gray-100 text-gray-600'}`}>{totalCount}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}



